import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Award,
  BarChart3,
  Bell,
  Boxes,
  Building2,
  Calculator,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CreditCard,
  FileSpreadsheet,
  FileText,
  FolderTree,
  GitMerge,
  LayoutDashboard,
  LogOut,
  Mail,
  Map,
  MapPin,
  Menu,
  MessageCircle,
  Package,
  Radar,
  Receipt,
  RotateCcw,
  Settings,
  Shield,
  ShoppingBag,
  Star,
  Store,
  Tags,
  Ticket,
  Truck,
  UserCircle,
  UserPlus,
  Users,
  Wallet,
  Warehouse,
  Wifi,
  X,
  Brain,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface MenuItem {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
  roles: string[];
  permission?: string;
}

interface MenuSection {
  title: string;
  icon: typeof LayoutDashboard;
  items: MenuItem[];
}

const roleLabels: Record<string, string> = {
  admin: 'مدير النظام',
  ceo: 'الإدارة التنفيذية',
  staff: 'موظف',
  delegate: 'مندوب',
  merchant: 'تاجر',
  support: 'دعم العملاء',
  customer: 'عميل',
};

export default function SidebarNew() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = String(user?.role || 'staff').toLowerCase();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSections, setOpenSections] = useState<string[]>([]);

  const menuSections = useMemo<MenuSection[]>(() => {
    if (role === 'merchant') {
      return [{
        title: 'الرئيسية',
        icon: LayoutDashboard,
        items: [
          { path: '/merchant/dashboard', label: 'لوحة التحكم', icon: LayoutDashboard, roles: ['merchant'] },
          { path: '/merchant/fulfillment', label: 'تجهيز الطلبات', icon: Boxes, roles: ['merchant'] },
          { path: '/ai-assistant', label: 'الوكيل الذكي', icon: Brain, roles: ['merchant'] },
          { path: '/accounting/cod', label: 'محاسبة التحصيل', icon: Wallet, roles: ['merchant'] },
          { path: '/accounting/statement', label: 'كشف الحساب', icon: FileText, roles: ['merchant'] },
          { path: '/coupons', label: 'الترويج والكوبونات', icon: Tags, roles: ['merchant'] },
          { path: '/shipments', label: 'شحناتي', icon: Package, roles: ['merchant'] },
          { path: '/shipments/pending', label: 'بانتظار الموافقة', icon: Clock, roles: ['merchant'] },
          { path: '/shipments/upload', label: 'رفع الشحنات', icon: FileSpreadsheet, roles: ['merchant'] },
        ],
      }];
    }

    if (role === 'delegate') {
      return [{
        title: 'الرئيسية',
        icon: LayoutDashboard,
        items: [
          { path: '/delegate/dashboard', label: 'لوحة التحكم', icon: LayoutDashboard, roles: ['delegate'] },
          { path: '/driver/opportunities', label: 'فرص العمل', icon: Wallet, roles: ['delegate'] },
          { path: '/ai-assistant', label: 'مساعد التشغيل', icon: Brain, roles: ['delegate'] },
          { path: '/delegate/shipments', label: 'شحناتي', icon: Package, roles: ['delegate'] },
          { path: '/delegate/tracking', label: 'تتبع موقعي', icon: Radar, roles: ['delegate'] },
          { path: '/notifications', label: 'الإشعارات', icon: Bell, roles: ['delegate'] },
        ],
      }];
    }

    if (role === 'ceo') {
      return [
        { title: 'الرئيسية', icon: LayoutDashboard, items: [
          { path: '/ceo-dashboard', label: 'لوحة المدير التنفيذي', icon: LayoutDashboard, roles: ['ceo'] },
          { path: '/dashboard', label: 'لوحة المؤشرات', icon: BarChart3, roles: ['ceo'] },
          { path: '/notifications', label: 'الإشعارات', icon: Bell, roles: ['ceo'] },
        ] },
        { title: 'التقارير والمراقبة', icon: BarChart3, items: [
          { path: '/reports', label: 'التقارير', icon: BarChart3, roles: ['ceo'] },
          { path: '/reports-advanced', label: 'التقارير المتقدمة', icon: BarChart3, roles: ['ceo'] },
          { path: '/analytics', label: 'التحليلات الذكية', icon: Brain, roles: ['ceo'] },
        ] },
      ];
    }

    return [
      { title: 'الرئيسية', icon: LayoutDashboard, items: [
        { path: '/dashboard', label: 'لوحة التحكم', icon: LayoutDashboard, roles: ['admin', 'staff'] },
        { path: '/notifications', label: 'الإشعارات', icon: Bell, roles: ['admin', 'staff'] },
      ] },
      { title: 'الشحن والعمليات', icon: Truck, items: [
        { path: '/shipments', label: 'الشحنات', icon: Package, roles: ['admin', 'staff'] },
        { path: '/shipments/awaiting-approval', label: 'الشحنات الواردة', icon: ClipboardCheck, roles: ['admin', 'staff'] },
        { path: '/merchant/fulfillment', label: 'تجهيز الطلبات', icon: Boxes, roles: ['admin', 'staff'] },
        { path: '/orders', label: 'طلبات المبيعات', icon: ShoppingBag, roles: ['admin', 'staff'] },
        { path: '/pickups', label: 'الاستلامات', icon: MapPin, roles: ['admin', 'staff'] },
        { path: '/drs', label: 'بيانات التسليم', icon: GitMerge, roles: ['admin', 'staff'] },
        { path: '/returns', label: 'المرتجعات', icon: RotateCcw, roles: ['admin', 'staff'] },
        { path: '/shipment-statuses', label: 'حالات الشحن', icon: ClipboardList, roles: ['admin', 'staff'] },
        { path: '/gps-tracking', label: 'تتبع المندوبين', icon: Radar, roles: ['admin', 'staff'] },
        { path: '/driver/opportunities', label: 'فرص عمل المناديب', icon: Wallet, roles: ['admin'] },
      ] },
      { title: 'العملاء والمندوبين', icon: Users, items: [
        { path: '/customers', label: 'العملاء', icon: Users, roles: ['admin', 'staff'] },
        { path: '/delegates', label: 'المندوبون', icon: Truck, roles: ['admin', 'staff'] },
        { path: '/employees', label: 'الموظفون', icon: Users, roles: ['admin', 'staff'] },
        { path: '/vans', label: 'سيارات التوزيع', icon: Truck, roles: ['admin', 'staff'] },
      ] },
      { title: 'المالية والمحاسبة', icon: Wallet, items: [
        { path: '/accounts-chart', label: 'شجرة الحسابات', icon: FolderTree, roles: ['admin', 'staff'] },
        { path: '/accounting-entries', label: 'القيود اليومية', icon: FileText, roles: ['admin', 'staff'] },
        { path: '/taxes', label: 'الضرائب', icon: Receipt, roles: ['admin', 'staff'] },
        { path: '/banks', label: 'البنوك', icon: Building2, roles: ['admin', 'staff'] },
        { path: '/receipts', label: 'المقبوضات', icon: CreditCard, roles: ['admin', 'staff'] },
        { path: '/invoices', label: 'الفواتير', icon: FileText, roles: ['admin', 'staff'] },
        { path: '/bank-accounts', label: 'الحسابات البنكية', icon: Wallet, roles: ['admin', 'staff'] },
        { path: '/payment-statuses', label: 'حالات الدفع', icon: CreditCard, roles: ['admin', 'staff'] },
        { path: '/financial-reports', label: 'التقارير المالية', icon: BarChart3, roles: ['admin', 'staff'] },
        { path: '/price-categories', label: 'فئات الأسعار', icon: Tags, roles: ['admin', 'staff'] },
        { path: '/coupons', label: 'الكوبونات', icon: Ticket, roles: ['admin', 'staff'] },
        { path: '/payments', label: 'بوابة الدفع', icon: CreditCard, roles: ['admin', 'staff'] },
        { path: '/accounting/cod', label: 'محاسبة التحصيل', icon: Wallet, roles: ['admin'] },
        { path: '/accounting/statement', label: 'كشف حساب العميل', icon: FileText, roles: ['admin'] },
        { path: '/shipments/upload', label: 'رفع شحنات (Excel)', icon: FileSpreadsheet, roles: ['admin', 'staff'] },
        { path: '/shipments/scan', label: 'مسح وتحديث الحالة', icon: FileText, roles: ['admin', 'staff'] },
        { path: '/branch-transfers', label: 'التحويل بين الفروع', icon: FileText, roles: ['admin', 'staff'] },
      ] },
      { title: 'المخازن والمنتجات', icon: Warehouse, items: [
        { path: '/products', label: 'المنتجات', icon: Package, roles: ['admin', 'staff'] },
        { path: '/warehouses', label: 'المخازن', icon: Warehouse, roles: ['admin', 'staff'] },
        { path: '/van-stocks', label: 'مخزون السيارات', icon: Package, roles: ['admin', 'staff'] },
      ] },
      { title: 'الولاء والتسويق', icon: Award, items: [
        { path: '/loyalty', label: 'برنامج الولاء', icon: Award, roles: ['admin', 'staff'] },
        { path: '/loyalty-pricing', label: 'الخصومات التلقائية', icon: Calculator, roles: ['admin', 'staff'] },
        { path: '/referrals', label: 'نظام الإحالات', icon: UserPlus, roles: ['admin', 'staff'] },
        { path: '/ratings', label: 'التقييمات', icon: Star, roles: ['admin', 'staff'] },
      ] },
      { title: 'المتجر والتواصل', icon: Store, items: [
        { path: '/store', label: 'المتجر الإلكتروني', icon: Store, roles: ['admin', 'staff', 'customer'] },
        { path: '/whatsapp', label: 'نظام WhatsApp', icon: MessageCircle, roles: ['admin', 'staff'] },
        { path: '/whatsapp-bot', label: 'إدارة البوت', icon: Wifi, roles: ['admin'] },
        { path: '/email', label: 'البريد الإلكتروني', icon: Mail, roles: ['admin', 'staff'] },
        { path: '/contact-messages', label: 'رسائل التواصل', icon: Mail, roles: ['admin', 'staff'] },
      ] },
      { title: 'المواقع والفروع', icon: Map, items: [
        { path: '/branches', label: 'الفروع', icon: Building2, roles: ['admin', 'staff'] },
        { path: '/cities', label: 'المدن', icon: Map, roles: ['admin', 'staff'] },
        { path: '/zones', label: 'المناطق', icon: MapPin, roles: ['admin', 'staff'] },
        { path: '/delegate-commissions', label: 'عمولات المناديب', icon: Wallet, roles: ['admin'] },
      ] },
      { title: 'الإدارة والأمان', icon: Shield, items: [
        { path: '/users', label: 'المستخدمون', icon: Users, roles: ['admin', 'staff'], permission: 'admin:users:read' },
        { path: '/roles', label: 'الصلاحيات', icon: FolderTree, roles: ['admin'] },
        { path: '/two-factor', label: 'المصادقة الثنائية', icon: Shield, roles: ['admin'] },
        { path: '/settings', label: 'الإعدادات', icon: Settings, roles: ['admin', 'staff'] },
        { path: '/integrations', label: 'التكاملات', icon: Boxes, roles: ['admin'] },
        { path: '/templates', label: 'القوالب', icon: FileText, roles: ['admin', 'staff'] },
      ] },
      { title: 'التقارير والمراقبة', icon: BarChart3, items: [
        { path: '/reports', label: 'التقارير', icon: BarChart3, roles: ['admin', 'staff'] },
        { path: '/reports-advanced', label: 'التقارير المتقدمة', icon: BarChart3, roles: ['admin', 'staff'] },
        { path: '/ceo-dashboard', label: 'لوحة المدير التنفيذي', icon: LayoutDashboard, roles: ['admin'] },
        { path: '/ai-assistant', label: 'الوكيل الذكي', icon: Brain, roles: ['admin'] },
        { path: '/analytics', label: 'التحليلات الذكية', icon: Brain, roles: ['admin', 'staff'] },
        { path: '/tickets', label: 'تذاكر الدعم', icon: MessageCircle, roles: ['admin', 'staff'] },
        { path: '/complaints', label: 'الشكاوى', icon: AlertCircle, roles: ['admin', 'staff'] },
        { path: '/audit-logs', label: 'سجلات التدقيق', icon: FileSpreadsheet, roles: ['admin'] },
      ] },
    ];
  }, [role]);

  const visibleSections = useMemo(() => menuSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        const roleAllowed = item.roles.includes(role);
        return roleAllowed && (!item.permission || role === 'admin' || hasPermission(item.permission));
      }),
    }))
    .filter((section) => section.items.length > 0), [hasPermission, menuSections, role]);

  useEffect(() => {
    const activeSectionTitles = visibleSections
      .filter((section) => section.items.some((item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)))
      .map((section) => section.title);
    setOpenSections((current) => Array.from(new Set([...current, ...activeSectionTitles])));
  }, [location.pathname, visibleSections]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleLogout = () => {
    if (window.confirm('هل أنت متأكد من تسجيل الخروج؟')) {
      logout();
      navigate('/login');
    }
  };

  const toggleSection = (title: string) => {
    setOpenSections((current) => current.includes(title) ? current.filter((item) => item !== title) : [...current, title]);
  };

  return (
    <>
      <button
        type="button"
        aria-label="فتح القائمة الجانبية"
        aria-controls="app-sidebar"
        aria-expanded={mobileOpen}
        onClick={() => setMobileOpen(true)}
        className="fixed right-4 top-4 z-50 rounded-lg bg-blue-600 p-2 text-white shadow-lg md:hidden"
      >
        <Menu className="h-6 w-6" aria-hidden="true" />
      </button>

      {mobileOpen && (
        <button
          type="button"
          aria-label="إغلاق القائمة الجانبية"
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-30 bg-slate-950/60 md:hidden"
        />
      )}

      <aside
        id="app-sidebar"
        aria-label="القائمة الرئيسية"
        className={`${mobileOpen ? 'translate-x-0' : 'translate-x-full'} fixed right-0 top-0 z-40 flex h-screen w-72 flex-col border-l border-slate-800 bg-slate-950 shadow-2xl transition-transform duration-300 md:relative md:translate-x-0`}
      >
        <div className="border-b border-slate-800 bg-slate-950 p-4">
          <div className="mb-4 flex items-center justify-center">
            <img src="/icon-512-from-server.png" alt="Target Express" className="h-20 w-auto object-contain" />
          </div>
          {user && (
            <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
              <div className="mb-1 flex items-center gap-2">
                <UserCircle className="h-5 w-5 text-blue-400" aria-hidden="true" />
                <span className="truncate text-sm font-semibold text-white">{user.fullName || user.username}</span>
              </div>
              <p className="text-xs text-slate-400">الدور: {roleLabels[role] || role}</p>
            </div>
          )}
          <button
            type="button"
            aria-label="إغلاق القائمة الجانبية"
            onClick={() => setMobileOpen(false)}
            className="absolute left-4 top-4 rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-white md:hidden"
          >
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <nav className="custom-scrollbar flex-1 space-y-1 overflow-y-auto p-3" aria-label="روابط النظام">
          {visibleSections.map((section) => {
            const isOpen = openSections.includes(section.title);
            const SectionIcon = section.icon;
            return (
              <div key={section.title} className="mb-1">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => toggleSection(section.title)}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-right ${isOpen ? 'border border-orange-600/30 bg-orange-600/20 text-orange-400' : 'text-slate-400 hover:bg-orange-600/20 hover:text-orange-400'}`}
                >
                  <span className="flex items-center gap-3">
                    <SectionIcon className="h-5 w-5" aria-hidden="true" />
                    <span className="text-sm font-semibold">{section.title}</span>
                  </span>
                  {isOpen ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
                </button>
                {isOpen && (
                  <ul className="mt-1 space-y-0.5 border-r border-slate-700 pr-3">
                    {section.items.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <li key={item.path}>
                          <NavLink
                            to={item.path}
                            state={{ newTab: true, title: item.label }}
                            onClick={() => setMobileOpen(false)}
                            className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm ${isActive ? 'bg-orange-600 font-medium text-white shadow-md shadow-orange-900/20' : 'text-slate-400 hover:bg-orange-600/20 hover:text-orange-400'}`}
                          >
                            <ItemIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                            <span className="truncate">{item.label}</span>
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-slate-800 bg-slate-950 p-4">
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-600/30 bg-red-600/10 px-4 py-2.5 font-semibold text-red-400 hover:border-red-600 hover:bg-red-600 hover:text-white"
          >
            <LogOut className="h-5 w-5" aria-hidden="true" />
            <span>تسجيل الخروج</span>
          </button>
        </div>
      </aside>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #0f172a; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #475569; }
      `}</style>
    </>
  );
}
